import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/pop-ui/pop-toast";
import { useORPC } from "@/providers/orpc-provider";
import { CodeInput, type CodeInputRef } from "../components/code-input";
import { Button } from "../components/form-items";
import { PhoneInput } from "../components/phone-input";
import { AREA_LIST, formatPhoneParam, type UserFormErrors, validatePhoneNumber } from "../utils";

// Helper function to extract error message
const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "message" in error) {
    return String(error.message);
  }
  if (error && typeof error === "object" && "data" in error && error.data && typeof error.data === "object" && "message" in error.data) {
    return String(error.data.message);
  }
  return "";
};
interface LinkPhoneFormProps {
  area: string;
  onFinish?: () => void;
}

// 绑定手机号表单
export function LinkPhoneForm(t0) {
  const $ = _c(70);
  const {
    area,
    onFinish
  } = t0;
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [showCodeInput, setShowCodeInput] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      phone: "",
      code: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [errors, setErrors] = useState(t1);
  const codeInputRef = useRef(null);
  const orpc = useORPC();
  const {
    t
  } = useTranslation();
  const phoneInputId = useId();
  let t2;
  if ($[1] !== orpc) {
    t2 = async phoneNumber => orpc.user.changePhoneOtp.call({
      phoneNumber
    }, {
      context: {
        headers: {}
      }
    });
    $[1] = orpc;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] !== t) {
    t3 = () => {
      setShowCodeInput(true);
      setTimeout(() => {
        codeInputRef.current?.startCountdown();
      }, 500);
      toast.success(t("toast.success.verificationCodeSentToYourPhone"));
    };
    t4 = error => {
      console.error("Send verification code failed:", error);
      const errorMessage = getErrorMessage(error) || t("toast.error.sendCodeFailedTryLater");
      toast.error(errorMessage);
    };
    $[3] = t;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      mutationFn: t2,
      onSuccess: t3,
      onError: t4
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    mutate: sendVerificationCode,
    isPending: isSendingCode
  } = useMutation(t5);
  let t6;
  if ($[10] !== orpc) {
    t6 = async params => orpc.user.bindPhone.call(params);
    $[10] = orpc;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onFinish || $[13] !== t) {
    t7 = () => {
      toast.success(t("toast.success.phoneLinkedSuccessfully"));
      if (onFinish) {
        onFinish();
      }
    };
    $[12] = onFinish;
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = error_0 => {
      console.error("Link phone failed:", error_0);
      const errorMessage_0 = getErrorMessage(error_0) || t("toast.error.linkPhoneFailedTryLater");
      toast.error(errorMessage_0);
    };
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t9 = {
      mutationFn: t6,
      onSuccess: t7,
      onError: t8
    };
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const {
    mutate: linkPhone,
    isPending: isLinking
  } = useMutation(t9);
  let t10;
  if ($[21] !== area || $[22] !== phone || $[23] !== sendVerificationCode || $[24] !== t) {
    t10 = async () => {
      setErrors({
        phone: "",
        code: ""
      });
      if (!phone) {
        setErrors(prev => ({
          ...prev,
          phone: t("error.enterPhoneNumber")
        }));
        return;
      }
      const cleanedInput = phone.replace(/\D/g, "");
      const result = validatePhoneNumber(cleanedInput, area);
      if (!result.valid) {
        setErrors(prev_0 => ({
          ...prev_0,
          phone: t("error.invalidPhoneNumber")
        }));
        return;
      }
      const areaInfo = AREA_LIST.find(item => item.code === area);
      if (areaInfo && result.regionCode !== areaInfo.phoneCode) {
        setErrors(prev_1 => ({
          ...prev_1,
          phone: t("error.invalidPhoneNumber")
        }));
        return;
      }
      const formattedPhone = formatPhoneParam(phone, area);
      sendVerificationCode(formattedPhone);
    };
    $[21] = area;
    $[22] = phone;
    $[23] = sendVerificationCode;
    $[24] = t;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleResendCode = t10;
  let t11;
  if ($[26] !== area || $[27] !== phone || $[28] !== sendVerificationCode || $[29] !== t) {
    t11 = async e => {
      e.preventDefault();
      setErrors({
        phone: "",
        code: ""
      });
      if (!phone) {
        setErrors(prev_2 => ({
          ...prev_2,
          phone: t("error.enterPhoneNumber")
        }));
        return;
      }
      const cleanedInput_0 = phone.replace(/\D/g, "");
      const result_0 = validatePhoneNumber(cleanedInput_0, area);
      if (!result_0.valid) {
        setErrors(prev_3 => ({
          ...prev_3,
          phone: t("error.invalidPhoneNumber")
        }));
        return;
      }
      const areaInfo_0 = AREA_LIST.find(item_0 => item_0.code === area);
      if (areaInfo_0 && result_0.regionCode !== areaInfo_0.phoneCode) {
        setErrors(prev_4 => ({
          ...prev_4,
          phone: t("error.invalidPhoneNumber")
        }));
        return;
      }
      const formattedPhone_0 = formatPhoneParam(phone, area);
      sendVerificationCode(formattedPhone_0);
    };
    $[26] = area;
    $[27] = phone;
    $[28] = sendVerificationCode;
    $[29] = t;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const handleSendCode = t11;
  let t12;
  if ($[31] !== area || $[32] !== code || $[33] !== linkPhone || $[34] !== phone || $[35] !== t) {
    t12 = e_0 => {
      e_0.preventDefault();
      setErrors({
        phone: "",
        code: ""
      });
      if (!code) {
        setErrors(prev_5 => ({
          ...prev_5,
          code: t("error.enterVerificationCode")
        }));
        return;
      } else {
        if (code.length !== 6) {
          setErrors(prev_6 => ({
            ...prev_6,
            code: t("error.enter6DigitVerificationCode")
          }));
          return;
        }
      }
      const formattedPhone_1 = formatPhoneParam(phone, area);
      linkPhone({
        phoneNumber: formattedPhone_1,
        otp: code
      });
    };
    $[31] = area;
    $[32] = code;
    $[33] = linkPhone;
    $[34] = phone;
    $[35] = t;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const handleSubmitLink = t12;
  const isLoading = isSendingCode || isLinking;
  const t13 = showCodeInput ? handleSubmitLink : handleSendCode;
  const t14 = isLoading || showCodeInput;
  let t15;
  if ($[37] !== t) {
    t15 = t("placeholder.phoneInput");
    $[37] = t;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const t16 = showCodeInput ? "bg-gray-100" : "";
  let t17;
  if ($[39] !== area || $[40] !== phone || $[41] !== phoneInputId || $[42] !== t14 || $[43] !== t15 || $[44] !== t16) {
    t17 = <PhoneInput id={phoneInputId} area={area} value={phone} onChange={setPhone} disabled={t14} placeholder={t15} className={t16} />;
    $[39] = area;
    $[40] = phone;
    $[41] = phoneInputId;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== errors.phone) {
    t18 = errors.phone && <p className="text-destructive text-sm leading-[1.5]">{errors.phone}</p>;
    $[46] = errors.phone;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t17 || $[49] !== t18) {
    t19 = <div className="space-y-2">{t17}{t18}</div>;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== code || $[52] !== errors.code || $[53] !== handleResendCode || $[54] !== isLoading || $[55] !== showCodeInput) {
    t20 = showCodeInput && <CodeInput ref={codeInputRef} value={code} onChange={setCode} onResend={handleResendCode} error={errors.code} disabled={isLoading} autoStartCountdown={false} />;
    $[51] = code;
    $[52] = errors.code;
    $[53] = handleResendCode;
    $[54] = isLoading;
    $[55] = showCodeInput;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== isLinking || $[58] !== isSendingCode || $[59] !== showCodeInput || $[60] !== t) {
    t21 = isSendingCode ? t("button.sending") : isLinking ? t("button.linking") : showCodeInput ? t("button.confirmLink") : t("button.sendCode");
    $[57] = isLinking;
    $[58] = isSendingCode;
    $[59] = showCodeInput;
    $[60] = t;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== isLoading || $[63] !== t21) {
    t22 = <Button type="submit" className="w-full" size="lg" loading={isLoading}>{t21}</Button>;
    $[62] = isLoading;
    $[63] = t21;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] !== t13 || $[66] !== t19 || $[67] !== t20 || $[68] !== t22) {
    t23 = <form onSubmit={t13} className="space-y-3">{t19}{t20}{t22}</form>;
    $[65] = t13;
    $[66] = t19;
    $[67] = t20;
    $[68] = t22;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  return t23;
}